import React from 'react';
import { connect } from 'react-redux';

import InputWrapper from 'components/form-multi/InputWrapper';
import { clearErrorField, notValidateUpdateValue, validationAndUpdateValue } from 'components/form-multi/utils';

import { changeFormField, setFieldError, setFieldVisible } from 'actions/formAction';

import { CARD_EXP_DATE, PHONE, SOURCE_CARD_EXP_DATE } from 'constants/fieldsName';
import { PAN } from 'constants/FieldType';
import { LOGIN_REGISTRATION_FORM_NAME } from 'constants/formName';

import update from 'immutability-helper';

import NewInputWrapper from './NewInputWrapper';

export default function InputBase(InputComponent, isDetached = false) {
    class ComposedInput extends React.Component {
        constructor(props) {
            super(props);
            this.state = {
                isFilled: !!props.field.value || props.focus || props.isFilled,
                isFocused: false,
                isEmptyValue: !props.field.value,
            };

            this.inputRef = React.createRef();
            this.wrapperRef = React.createRef();

            this.handlerBlur = this.handlerBlur.bind(this);
            this.handlerChange = this.handlerChange.bind(this);
            this.handlerFocus = this.handlerFocus.bind(this);
            this.toggleFilled = this.toggleFilled.bind(this);
            this.focus = this.focus.bind(this);
        }

        handlerBlur(e) {
            this.setState(
                update(this.state, {
                    isFilled: { $set: !!e.target.value },
                    isEmptyValue: { $set: !e.target.value },
                    isFocused: { $set: false },
                }),
            );
            if (this.props.validateOnBlur) {
                const value = this.props.isFormattedInput ? e.target.value?.toString().replace(/\s+/g, '') : e.target.value;
                let field = this.props.field;
                validationAndUpdateValue(this.props.formName, value, field, this.props.changeFormField).then(() => {
                    if (this.props.onBlurHandler) {
                        this.props.onBlurHandler(value, field.name, field.checkRegexp, field.mandatory);
                    }
                });
            }
        }

        handlerChange(e) {
            notValidateUpdateValue(this.props.formName, e.target.value, this.props.field, this.props.changeFormField).then(() => {
                if (this.props.onChangeHandler) {
                    this.props.onChangeHandler(this.props.field.value, this.props.field.name);
                }
            });
        }

        handlerFocus(e) {
            this.setState(
                update(this.state, {
                    isFilled: { $set: true },
                    isFocused: { $set: true },
                }),
            );
            const isError = this.props.field.isError;
            if (this.props.isClearErrorOnFocus && isError) {
                clearErrorField(this.props.formName, this.props.field, this.props.changeFormField);
            }
            if (this.props.onFocus) {
                this.props.onFocus(e);
            }
        }

        toggleFilled() {
            if (!this.props.field.value && this.props.field.type === PAN) {
                this.setState(
                    update(this.state, {
                        isFilled: { $set: Boolean(this.props.field.value) },
                    }),
                );
            }
        }

        focus(e) {
            if (e.currentTarget) {
                const input = e.currentTarget.firstElementChild;
                input.focus();
            } else {
                console.log('Focus method not support');
            }
        }

        componentDidMount() {
            if (LOGIN_REGISTRATION_FORM_NAME !== this.props.formName) {
                this.props.setFieldVisible(this.props.formName, this.props.field.name);
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.defaultCountry && this.props.defaultCountry !== prevProps.defaultCountry && !this.props.field?.value) {
                this.setState(
                    update(this.state, {
                        isFilled: { $set: !this.props.field.value },
                    }),
                );
            }
            if (this.props.field !== undefined && this.props.field.value !== prevProps.field.value && !Number.isNaN(this.props.field.value)) {
                const valueLength = this.props.field?.value?.length;
                const fieldName = this.props.field?.name;
                const isUpdateIsFilled = valueLength === 0 && (fieldName === PHONE || fieldName === CARD_EXP_DATE || fieldName === SOURCE_CARD_EXP_DATE);
                if (!isUpdateIsFilled) {
                    this.setState(
                        update(this.state, {
                            isFilled: { $set: !!this.props.field.value },
                        }),
                    );
                }
            }
            if (this.props.isNewInput && this.props.isFocused !== prevProps.isFocused) {
                this.setState(
                    update(this.state, {
                        isFocused: { $set: this.props.isFocused },
                    }),
                );
            }

            if (this.props.field !== undefined && this.props.scrollToField !== prevProps.scrollToField && this.props.field.name === this.props.scrollToField) {
                this.wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        render() {
            const field = this.props.field;
            const isDetachedVar = this.props.isDetached ? this.props.isDetached : isDetached;

            if (field === undefined) {
                return false;
            }

            return (
                <>
                    {!this.props.isNewInput && (
                        <InputWrapper
                            forwardedRef={this.wrapperRef}
                            isReadOnly={field.isReadOnly}
                            longTitle={field.longTitle}
                            comment={field.comment}
                            commentLong={field.commentLong}
                            name={field.name}
                            isFilled={this.state.isFilled}
                            isEmptyValue={this.state.isEmptyValue}
                            isError={field.isError}
                            isDetached={isDetachedVar}
                            labelText={field.placeholder}
                            cssTitleClass={this.props.cssTitleClass}
                            wrapperClassName={this.props.wrapperClassName}
                            errorMessage={field.errorMessage}
                            childrenInLabel={this.props.childrenInLabel}
                            hideBorder={this.props.hideBorder}
                            setFocus={this.focus}
                            focus={this.props.focus}
                            isLanding={this.props.isLanding}
                        >
                            <InputComponent
                                {...this.props}
                                inputBaseHandlerBlur={this.handlerBlur}
                                onFocus={this.handlerFocus}
                                onBlur={this.handlerBlur}
                                onChange={this.handlerChange}
                                toggleFilled={this.toggleFilled}
                                forwardedRef={this.inputRef}
                            />
                            {this.props.children}
                        </InputWrapper>
                    )}
                    {this.props.isNewInput && (
                        <NewInputWrapper
                            forwardedRef={this.wrapperRef}
                            isReadOnly={this.props.isReadOnly || field.isReadOnly}
                            longTitle={field.longTitle}
                            comment={field.comment}
                            commentLong={field.commentLong}
                            name={field.name}
                            isFilled={this.state.isFilled}
                            isFocused={this.state.isFocused}
                            isEmptyValue={this.state.isEmptyValue}
                            isError={field.isError}
                            hasArrowButton={this.props.hasArrowButton}
                            arrowButtonClick={this.props.arrowButtonClick}
                            isDetached={isDetachedVar}
                            labelText={field.placeholder}
                            errorMessage={field.errorMessage}
                            setFocus={this.focus}
                            focus={this.props.focus}
                            isLinedInput={this.props.isLinedInput}
                            isPseudoSelect={this.props.isPseudoSelect}
                            isSmsInput={this.props.isSmsInput}
                            isCenteredInput={this.props.isCenteredInput}
                            leftInputComponent={this.props.leftInputComponent}
                            isCalculatorInput={this.props.isCalculatorInput}
                            regexpErrorMessage={field.checkRegexpError}
                            isLoginInputError={this.props.isLoginInputError}
                            hintButtonClick={this.props.hintButtonClick}
                        >
                            <InputComponent
                                {...this.props}
                                inputBaseHandlerBlur={this.handlerBlur}
                                onFocus={this.handlerFocus}
                                onBlur={this.handlerBlur}
                                onChange={this.handlerChange}
                                toggleFilled={this.toggleFilled}
                                forwardedRef={this.inputRef}
                            />
                            {this.props.children}
                        </NewInputWrapper>
                    )}
                </>
            );
        }
    }

    ComposedInput.defaultProps = {
        validateOnBlur: true,
        isDetached: false,
        isNewInput: false,
        isFormattedInput: false,
        isFocused: false,
    };

    const mapStateToProps = ({ form, messages }, ownProps) => ({
        scrollToField: form[ownProps.formName]?.scrollToField,
        jsMessages: messages.jsMessages,
    });

    return connect(mapStateToProps, { changeFormField, setFieldVisible, setFieldError })(ComposedInput);
}
